import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../../context/AuthContext';
import { useDebouncedValue } from '../../shared/useDebouncedValue';
import {
  Button,
  Callout,
  Combobox,
  Dialog,
  EmptyState,
  Field,
  IconButton,
  IconCheck,
  IconMore,
  IconPhone,
  IconPlus,
  Input,
  Menu,
  MenuItem,
  ListPage,
  Pagination,
  Row,
  SectionTitle,
  Select,
  StatusLabel,
  Table,
  TableSkeletonRows,
  Tag,
  TBody,
  TD,
  TH,
  THead,
  TR,
  TwoLine,
} from '../../ui/v2';
import { api, apiEndpoints } from '../../utils/apiService';
import {
  type TableSortState,
  toOrderingParam,
  toggleSortState,
} from '../../utils/listOrdering';
import { TableSearch } from './TableSearch';
import {
  buildPurchasePayload,
  capabilitiesSummary,
  DEFAULT_SEARCH_LIMIT,
  DEFAULT_SMS_URL,
  DEFAULT_TRUNK_SID,
  deleteTfvSuccessMessage,
  displayValue,
  EMPTY_PHONE_NUMBERS_PAGE,
  inventoryOptions as toInventoryOptions,
  isDeletableTfvSid,
  numberCapabilities,
  parseInventory,
  parsePhoneNumbersPage,
  type PhoneNumberRegulatoryStatus,
  PHONE_NUMBERS_PAGE_SIZE,
  type PurchaseResponse,
  purchaseSuccessMessage,
  readSelectedStoreId,
  responseErrorMessage,
  SMS_METHODS,
  tfvSid,
  tfvStatusLabel,
  tfvStatusTone,
  type TollFreeInventoryNumber,
  webhookLine,
} from './twilioPhoneNumbersModel';

export { twilioSmsWebhookUrl } from './twilioPhoneNumbersModel';

const Dash: React.FC = () => <span className="text-v2-fg-4">—</span>;

type PhoneSortKey =
  | 'phone_number'
  | 'friendly_name'
  | 'capabilities'
  | 'active_voice_config'
  | 'active_messaging_config'
  | 'tollfree_verification_status';

const PHONE_SORT_KEY_TO_FIELD: Record<PhoneSortKey, string> = {
  phone_number: 'phone_number',
  friendly_name: 'friendly_name',
  capabilities: 'capabilities',
  active_voice_config: 'active_voice_config',
  active_messaging_config: 'active_messaging_config',
  tollfree_verification_status: 'tollfree_verification_status',
};

const PHONE_COLUMNS: { key: PhoneSortKey; label: string; width?: number }[] = [
  { key: 'phone_number', label: 'Number' },
  { key: 'friendly_name', label: 'Friendly name' },
  { key: 'capabilities', label: 'Capabilities', width: 180 },
  { key: 'active_voice_config', label: 'Voice config' },
  { key: 'active_messaging_config', label: 'Messaging config' },
  { key: 'tollfree_verification_status', label: 'TFV status', width: 200 },
];

const CELL = 'py-2 align-top';

const TwilioPhoneNumberPurchaseTab: React.FC = () => {
  const { user } = useAuth();
  const [phoneNumbers, setPhoneNumbers] = useState<
    PhoneNumberRegulatoryStatus[]
  >([]);
  const [phoneNumbersPage, setPhoneNumbersPage] = useState(1);
  const [phoneNumbersTotalPages, setPhoneNumbersTotalPages] = useState(1);
  const [phoneNumbersCount, setPhoneNumbersCount] = useState(0);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);
  const [phoneSearch, setPhoneSearch] = useState('');
  const debouncedPhoneSearch = useDebouncedValue(phoneSearch.trim());
  const [phoneSort, setPhoneSort] = useState<TableSortState<PhoneSortKey>>({
    key: null,
    direction: 'asc',
  });
  const phoneOrdering = toOrderingParam(phoneSort, PHONE_SORT_KEY_TO_FIELD);
  const [phoneNumbersError, setPhoneNumbersError] = useState('');
  const [phoneNumberActionMenuId, setPhoneNumberActionMenuId] = useState<
    string | null
  >(null);
  const [deleteTfvTarget, setDeleteTfvTarget] =
    useState<PhoneNumberRegulatoryStatus | null>(null);
  const [deletingTfvSid, setDeletingTfvSid] = useState<string | null>(null);
  const [phoneNumberActionError, setPhoneNumberActionError] = useState('');
  const [phoneNumberActionSuccess, setPhoneNumberActionSuccess] = useState('');
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [limit, setLimit] = useState(DEFAULT_SEARCH_LIMIT);
  const [inventory, setInventory] = useState<TollFreeInventoryNumber[]>([]);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState('');
  const [friendlyName, setFriendlyName] = useState('');
  const [smsUrl, setSmsUrl] = useState(DEFAULT_SMS_URL);
  const [smsMethod, setSmsMethod] = useState('POST');
  const [trunkSid, setTrunkSid] = useState(DEFAULT_TRUNK_SID);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [purchaseResult, setPurchaseResult] = useState<PurchaseResponse | null>(
    null,
  );

  const inventoryOptions = useMemo(
    () => toInventoryOptions(inventory),
    [inventory],
  );

  const selectedInventoryNumber = useMemo(
    () =>
      inventory.find((number) => number.phone_number === selectedPhoneNumber) ||
      null,
    [inventory, selectedPhoneNumber],
  );

  const deleteTfvSid = tfvSid(deleteTfvTarget);
  const canDeleteTfv = isDeletableTfvSid(deleteTfvSid);

  const fetchPhoneNumbers = useCallback(async () => {
    if (!user?.is_platform_admin) return;

    const applyPage = (page: typeof EMPTY_PHONE_NUMBERS_PAGE) => {
      setPhoneNumbers(page.rows);
      setPhoneNumbersCount(page.count);
      setPhoneNumbersTotalPages(page.totalPages);
    };

    setLoadingPhoneNumbers(true);
    setPhoneNumbersError('');
    try {
      const response = await api.get(
        apiEndpoints.getPhoneNumberRegulatoryStatuses(
          phoneNumbersPage,
          PHONE_NUMBERS_PAGE_SIZE,
          debouncedPhoneSearch || undefined,
          phoneOrdering ?? undefined,
        ),
      );
      if (response?.ok) {
        applyPage(parsePhoneNumbersPage(await response.json()));
      } else if (response) {
        setPhoneNumbersError(
          await responseErrorMessage(
            response,
            `Failed to load phone numbers (Status: ${response.status})`,
          ),
        );
        applyPage(EMPTY_PHONE_NUMBERS_PAGE);
      } else {
        setPhoneNumbersError('Failed to load phone numbers: No response');
        applyPage(EMPTY_PHONE_NUMBERS_PAGE);
      }
    } catch (err) {
      console.error('Phone number status load error:', err);
      setPhoneNumbersError(
        'An unexpected error occurred loading phone numbers.',
      );
      applyPage(EMPTY_PHONE_NUMBERS_PAGE);
    } finally {
      setLoadingPhoneNumbers(false);
    }
  }, [
    debouncedPhoneSearch,
    phoneNumbersPage,
    phoneOrdering,
    user?.is_platform_admin,
  ]);

  useEffect(() => {
    fetchPhoneNumbers();
  }, [fetchPhoneNumbers]);

  const searchInventory = useCallback(async () => {
    setLoadingInventory(true);
    setError('');
    setSuccess('');
    setPurchaseResult(null);

    try {
      const response = await api.get(apiEndpoints.searchTollFreeNumbers(limit));
      if (response?.ok) {
        const numbers = parseInventory(await response.json());
        setInventory(numbers);
        setSelectedPhoneNumber(numbers[0]?.phone_number || '');
      } else if (response) {
        setError(
          await responseErrorMessage(
            response,
            `Failed to search toll-free numbers (Status: ${response.status})`,
          ),
        );
        setInventory([]);
      } else {
        setError('Failed to search toll-free numbers: No response from server');
        setInventory([]);
      }
    } catch (err) {
      console.error('Toll-free inventory search error:', err);
      setError('An unexpected error occurred while searching inventory.');
      setInventory([]);
    } finally {
      setLoadingInventory(false);
    }
  }, [limit]);

  const purchaseNumber = async () => {
    setError('');
    setSuccess('');
    setPurchaseResult(null);

    if (!readSelectedStoreId()) {
      setError(
        'Select a store from the global store selector before purchasing.',
      );
      return;
    }

    if (!selectedPhoneNumber) {
      setError('Select a phone number to purchase.');
      return;
    }

    setPurchasing(true);
    try {
      const response = await api.post(
        apiEndpoints.purchasePhoneNumber(),
        buildPurchasePayload({
          phoneNumber: selectedPhoneNumber,
          friendlyName,
          smsUrl,
          smsMethod,
          trunkSid,
        }),
      );
      if (response?.ok) {
        const data: PurchaseResponse = await response.json();
        setPurchaseResult(data);
        setSuccess(purchaseSuccessMessage(data));
        fetchPhoneNumbers();
      } else if (response) {
        setError(
          await responseErrorMessage(
            response,
            `Failed to purchase phone number (Status: ${response.status})`,
          ),
        );
      } else {
        setError('Failed to purchase phone number: No response from server');
      }
    } catch (err) {
      console.error('Phone number purchase error:', err);
      setError(
        'An unexpected error occurred while purchasing the phone number.',
      );
    } finally {
      setPurchasing(false);
    }
  };

  const confirmDeleteTfvApplication = async () => {
    if (!deleteTfvTarget) return;

    const verificationSid = deleteTfvTarget.tollfree_verification_sid?.trim();
    setPhoneNumberActionError('');
    setPhoneNumberActionSuccess('');

    if (!verificationSid || !isDeletableTfvSid(verificationSid)) {
      setPhoneNumberActionError(
        'Cannot delete TFV application without an HH verification SID.',
      );
      setDeleteTfvTarget(null);
      return;
    }

    setDeletingTfvSid(verificationSid);
    try {
      const response = await api.delete(
        apiEndpoints.deleteTollFreeVerification(verificationSid),
      );
      if (response?.ok) {
        const data = await response.json().catch(() => ({}));
        setPhoneNumberActionSuccess(
          deleteTfvSuccessMessage(data, verificationSid),
        );
        setDeleteTfvTarget(null);
        await fetchPhoneNumbers();
      } else if (response) {
        setPhoneNumberActionError(
          await responseErrorMessage(
            response,
            `Failed to delete TFV application (Status: ${response.status})`,
          ),
        );
      } else {
        setPhoneNumberActionError(
          'Failed to delete TFV application: No response from server',
        );
      }
    } catch (err) {
      console.error('TFV application delete error:', err);
      setPhoneNumberActionError(
        'An unexpected error occurred deleting the TFV application.',
      );
    } finally {
      setDeletingTfvSid(null);
    }
  };

  if (!user?.is_platform_admin) {
    return (
      <ListPage title="Purchase phone numbers">
        <div className="p-4 lg:p-6">
          <Callout tone="danger">
            This tool is only available to platform admins.
          </Callout>
        </div>
      </ListPage>
    );
  }

  const hasNotices =
    !!phoneNumbersError ||
    !!phoneNumberActionError ||
    !!phoneNumberActionSuccess;

  return (
    <>
      <ListPage
        title="Phone numbers"
        description="Review active Twilio numbers, voice/messaging configuration, TFV status, and available administrative actions."
        toolbar={
          <div className="flex min-h-11 items-center px-4 py-2 lg:px-6">
            <TableSearch
              label="Search phone numbers"
              placeholder="Search number, name, or verification status…"
              value={phoneSearch}
              onChange={(event) => {
                setPhoneSearch(event.target.value);
                setPhoneNumbersPage(1);
              }}
            />
          </div>
        }
        actions={
          <Button
            variant="primary"
            leading={<IconPlus size={14} />}
            onClick={() => setIsPurchaseModalOpen(true)}
          >
            Purchase toll-free number
          </Button>
        }
      >
        {hasNotices && (
          <div className="flex flex-col gap-2 px-4 pt-4 lg:px-6">
            {phoneNumbersError && (
              <Callout tone="danger">{phoneNumbersError}</Callout>
            )}
            {phoneNumberActionError && (
              <Callout tone="danger">{phoneNumberActionError}</Callout>
            )}
            {phoneNumberActionSuccess && (
              <Callout tone="accent" icon={<IconCheck size={14} />}>
                {phoneNumberActionSuccess}
              </Callout>
            )}
          </div>
        )}

        {!loadingPhoneNumbers && phoneNumbers.length === 0 ? (
          <EmptyState
            icon={<IconPhone />}
            title={
              debouncedPhoneSearch
                ? 'No phone numbers match that search'
                : 'No active phone numbers found.'
            }
            description={
              debouncedPhoneSearch
                ? 'Try a number, friendly name, or verification status.'
                : undefined
            }
          />
        ) : (
          <>
            {loadingPhoneNumbers && (
              <p role="status" className="sr-only">
                Loading phone numbers…
              </p>
            )}
            <div className="v2-scroll overflow-x-auto">
              <Table className="min-w-[1400px]">
                <THead>
                  <tr>
                    {PHONE_COLUMNS.map((column) => (
                      <TH
                        key={column.key}
                        width={column.width}
                        sortable={!loadingPhoneNumbers}
                        active={phoneSort.key === column.key}
                        dir={phoneSort.direction}
                        onSort={() => {
                          setPhoneSort((current) =>
                            toggleSortState(current, column.key),
                          );
                          setPhoneNumbersPage(1);
                        }}
                      >
                        {column.label}
                      </TH>
                    ))}
                    <TH width={220}>TFV profile</TH>
                    <TH width={64} align="center">
                      Actions
                    </TH>
                  </tr>
                </THead>
                <TBody>
                  {loadingPhoneNumbers ? (
                    <TableSkeletonRows cols={8} />
                  ) : (
                    phoneNumbers.map((number) => {
                      const rowId =
                        number.phone_number_sid || number.phone_number;
                      const verificationSid = tfvSid(number);
                      const rejectionReason = displayValue(
                        number.tollfree_rejection_reason,
                      );
                      const errorCode = displayValue(
                        number.tollfree_error_code,
                      );
                      return (
                        <TR key={rowId}>
                          <TD className={CELL}>
                            <TwoLine
                              primary={
                                <span className="v2-num">
                                  {number.phone_number}
                                </span>
                              }
                              secondary={number.phone_number_sid}
                            />
                            {number.is_toll_free && (
                              <Tag className="mt-1">Toll-free</Tag>
                            )}
                          </TD>
                          <TD className={`${CELL} text-v2-fg-2`}>
                            {number.friendly_name || <Dash />}
                          </TD>
                          <TD className={CELL}>
                            <div className="flex flex-wrap gap-1.5">
                              {numberCapabilities(number).map(
                                ({ label, enabled }) => (
                                  <Tag
                                    key={label}
                                    tone={enabled ? 'accent' : 'neutral'}
                                    className={
                                      enabled
                                        ? undefined
                                        : 'text-v2-fg-4 line-through'
                                    }
                                  >
                                    {label}
                                    {!enabled && (
                                      <span className="sr-only">
                                        {' '}
                                        (not enabled)
                                      </span>
                                    )}
                                  </Tag>
                                ),
                              )}
                            </div>
                          </TD>
                          <TD className={CELL}>
                            <ConfigCell
                              config={number.active_voice_config}
                              webhook={webhookLine(
                                number.voice_url,
                                number.voice_method,
                              )}
                            />
                          </TD>
                          <TD className={CELL}>
                            <ConfigCell
                              config={number.active_messaging_config}
                              webhook={webhookLine(
                                number.sms_url,
                                number.sms_method,
                              )}
                            />
                          </TD>
                          <TD className={CELL}>
                            <StatusLabel
                              size="sm"
                              tone={tfvStatusTone(
                                number.tollfree_verification_status,
                              )}
                            >
                              {tfvStatusLabel(number)}
                            </StatusLabel>
                            {rejectionReason && (
                              <div className="mt-1 text-v2-12 text-v2-danger">
                                {rejectionReason}
                              </div>
                            )}
                            {errorCode && (
                              <div className="mt-1 text-v2-12 text-v2-danger">
                                {errorCode}
                              </div>
                            )}
                          </TD>
                          <TD className={CELL}>
                            {number.tollfree_customer_profile_sid ? (
                              <TwoLine
                                primary={
                                  number.tollfree_customer_profile_name || (
                                    <Dash />
                                  )
                                }
                                secondary={number.tollfree_customer_profile_sid}
                              />
                            ) : (
                              <Dash />
                            )}
                          </TD>
                          <TD align="center" className={CELL}>
                            <RowActions
                              label={`Actions for ${number.phone_number}`}
                              open={phoneNumberActionMenuId === rowId}
                              onToggle={() =>
                                setPhoneNumberActionMenuId(
                                  phoneNumberActionMenuId === rowId
                                    ? null
                                    : rowId,
                                )
                              }
                              onClose={() => setPhoneNumberActionMenuId(null)}
                            >
                              <MenuItem
                                tone="danger"
                                disabled={
                                  !isDeletableTfvSid(verificationSid) ||
                                  deletingTfvSid === verificationSid
                                }
                                title={
                                  isDeletableTfvSid(verificationSid)
                                    ? undefined
                                    : 'Requires an HH verification SID'
                                }
                                onClick={() => {
                                  setPhoneNumberActionMenuId(null);
                                  setPhoneNumberActionError('');
                                  setPhoneNumberActionSuccess('');
                                  setDeleteTfvTarget(number);
                                }}
                              >
                                {deletingTfvSid === verificationSid
                                  ? 'Deleting…'
                                  : 'Delete TFV application'}
                              </MenuItem>
                            </RowActions>
                          </TD>
                        </TR>
                      );
                    })
                  )}
                </TBody>
              </Table>
            </div>
          </>
        )}

        <Pagination
          page={phoneNumbersPage}
          totalPages={phoneNumbersTotalPages}
          totalCount={phoneNumbersCount}
          pageSize={PHONE_NUMBERS_PAGE_SIZE}
          onPage={(page) => {
            if (loadingPhoneNumbers) return;
            setPhoneNumbersPage(
              Math.min(phoneNumbersTotalPages, Math.max(1, page)),
            );
          }}
          noun="numbers"
        />
      </ListPage>

      <Dialog
        open={isPurchaseModalOpen}
        onClose={() => setIsPurchaseModalOpen(false)}
        size="lg"
        title="Purchase toll-free number"
        description="Search inventory, choose an available number, and optionally override the default Twilio configuration."
        footer={
          <Button
            variant="primary"
            onClick={purchaseNumber}
            disabledReason={
              selectedPhoneNumber
                ? undefined
                : 'Select a phone number to purchase.'
            }
            loading={purchasing}
          >
            {purchasing ? 'Purchasing…' : 'Purchase selected number'}
          </Button>
        }
      >
        <div className="flex flex-col gap-5">
          <section aria-label="Search inventory">
            <SectionTitle>Search inventory</SectionTitle>
            <div className="mt-2 flex items-end gap-2">
              <Field label="Search limit" className="w-40">
                {(id, describedBy) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    type="number"
                    min="1"
                    max="50"
                    value={limit}
                    onChange={(e) =>
                      setLimit(Number(e.target.value) || DEFAULT_SEARCH_LIMIT)
                    }
                    disabled={loadingInventory || purchasing}
                    className="v2-num"
                  />
                )}
              </Field>
              <Button
                onClick={searchInventory}
                disabled={purchasing}
                loading={loadingInventory}
                data-autofocus="true"
              >
                {loadingInventory ? 'Searching…' : 'Search inventory'}
              </Button>
            </div>
          </section>

          <section
            aria-label="Available toll-free inventory"
            className="flex flex-col gap-4 border-t border-v2-line pt-4"
          >
            <SectionTitle>Available toll-free inventory</SectionTitle>
            <Field
              label="Phone number"
              hint={
                selectedInventoryNumber
                  ? `Capabilities: ${capabilitiesSummary(
                      selectedInventoryNumber,
                    )}.`
                  : 'Search inventory to load purchasable toll-free numbers.'
              }
            >
              {(id, describedBy) => (
                <Combobox
                  id={id}
                  aria-describedby={describedBy}
                  options={inventoryOptions}
                  value={selectedPhoneNumber}
                  onChange={setSelectedPhoneNumber}
                  placeholder={
                    loadingInventory
                      ? 'Loading inventory…'
                      : 'Select an available number'
                  }
                  disabled={
                    loadingInventory || purchasing || inventory.length === 0
                  }
                />
              )}
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Friendly name override">
                {(id, describedBy) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    type="text"
                    value={friendlyName}
                    onChange={(e) => setFriendlyName(e.target.value)}
                    placeholder="Default: AskArthur - selected store"
                    disabled={purchasing}
                  />
                )}
              </Field>
              <Field label="SMS URL override">
                {(id, describedBy) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    type="text"
                    value={smsUrl}
                    onChange={(e) => setSmsUrl(e.target.value)}
                    placeholder={DEFAULT_SMS_URL}
                    disabled={purchasing}
                  />
                )}
              </Field>
              <Field label="SMS method">
                {(id, describedBy) => (
                  <Select
                    id={id}
                    aria-describedby={describedBy}
                    value={smsMethod}
                    onChange={(e) => setSmsMethod(e.target.value)}
                    disabled={purchasing}
                  >
                    {SMS_METHODS.map((method) => (
                      <option key={method} value={method}>
                        {method}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Trunk SID override">
                {(id, describedBy) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    type="text"
                    value={trunkSid}
                    onChange={(e) => setTrunkSid(e.target.value)}
                    placeholder="TK..."
                    disabled={purchasing}
                  />
                )}
              </Field>
            </div>
          </section>

          {error && <Callout tone="danger">{error}</Callout>}
          {success && (
            <Callout tone="accent" icon={<IconCheck size={14} />}>
              {success}
            </Callout>
          )}

          {purchaseResult && (
            <section
              aria-label="Purchased number"
              className="border-t border-v2-line pt-4"
            >
              <SectionTitle>Purchased number</SectionTitle>
              <dl className="mt-1">
                <Row label="Phone number">
                  <span className="v2-num">{purchaseResult.phone_number}</span>
                </Row>
                <Row label="SID">
                  <span className="break-all">{purchaseResult.sid}</span>
                </Row>
                <Row label="Friendly name">
                  {purchaseResult.friendly_name || 'N/A'}
                </Row>
                <Row label="SMS URL">
                  <span className="break-all">
                    {purchaseResult.sms_url || 'N/A'}
                  </span>
                </Row>
                <Row label="SMS method">
                  {purchaseResult.sms_method || 'N/A'}
                </Row>
                <Row label="Trunk SID">
                  <span className="break-all">
                    {purchaseResult.trunk_sid || 'N/A'}
                  </span>
                </Row>
              </dl>
            </section>
          )}
        </div>
      </Dialog>

      <Dialog
        open={!!deleteTfvTarget}
        onClose={() => setDeleteTfvTarget(null)}
        dismissible={!deletingTfvSid}
        size="sm"
        title="Delete TFV application"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setDeleteTfvTarget(null)}
              disabled={!!deletingTfvSid}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={confirmDeleteTfvApplication}
              disabledReason={
                canDeleteTfv ? undefined : 'Requires an HH verification SID'
              }
              loading={!!deletingTfvSid}
            >
              {deletingTfvSid ? 'Deleting…' : 'Delete TFV application'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="text-v2-13 leading-5 text-v2-fg-2">
            This deletes the Twilio toll-free verification application for{' '}
            <span className="font-medium text-v2-fg">
              {deleteTfvTarget?.phone_number || 'this number'}
            </span>{' '}
            and marks matching local verification requests as deleted.
          </p>

          <dl className="border-y border-v2-line">
            <Row label="Verification SID" empty={!deleteTfvSid}>
              <span className="v2-num break-all">{deleteTfvSid || 'N/A'}</span>
            </Row>
          </dl>
          {!canDeleteTfv && (
            <Callout tone="warning">
              A TFV application can only be deleted when the verification SID
              starts with HH.
            </Callout>
          )}

          {phoneNumberActionError && (
            <Callout tone="danger">{phoneNumberActionError}</Callout>
          )}
        </div>
      </Dialog>
    </>
  );
};

const ConfigCell: React.FC<{
  config?: string | null;
  webhook: string;
}> = ({ config, webhook }) => (
  <>
    <div className="text-v2-fg-2">{config || <Dash />}</div>
    {webhook && (
      <div className="mt-0.5 break-all text-v2-12 text-v2-fg-3">{webhook}</div>
    )}
  </>
);

/** The ··· menu at the end of a row; the tab keeps which row's menu is open. */
const RowActions: React.FC<{
  label: string;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ label, open, onToggle, onClose, children }) => {
  const anchor = useRef<HTMLButtonElement>(null);
  return (
    <span className="relative inline-block text-left">
      <IconButton
        ref={anchor}
        size="sm"
        label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={onToggle}
      >
        <IconMore />
      </IconButton>
      <Menu
        open={open}
        onClose={onClose}
        anchorRef={anchor}
        align="right"
        width={200}
      >
        {children}
      </Menu>
    </span>
  );
};

export default TwilioPhoneNumberPurchaseTab;
